import { createApi } from '@reduxjs/toolkit/query/react'
import { createAuthBaseQuery } from './base-query'
import { COMPANION_API } from '@/config/constants'
import {
  SearchHitsSchema,
  RunSnapshotsResponseSchema,
  type SearchHits,
  type VisualSnapshot,
  DeepResearchSessionListResponseSchema,
  DeepResearchSessionDetailResponseSchema,
  SavedReportListResponseSchema,
  SavedReportResponseSchema,
  type SavedReport,
  type SavedReportSummary,
  type DeepResearchSession,
  type DeepResearchSessionDetail,
} from '@/features/deep-research/schemas/deep-research-types'
import {
  NewsSummarySchema,
  SecFilingSummarySchema,
  TickerSummarySchema,
  TranscriptEnrichmentSchema,
  TranscriptHighlightsSchema,
  KeyHighlightsResponseSchema,
  AutoDetectedHighlightsResponseSchema,
  TakeawaysResponseSchema,
  QaPairsResponseSchema,
} from '@/types/schemas/api'
import type {
  NewsSummary,
  SecFilingSummary,
  TickerSummary,
  TranscriptEnrichment,
  TranscriptHighlights,
  SummarizeTranscriptHighlightsRequest,
  KeyHighlightsResponse,
  AutoDetectedHighlightsResponse,
  TakeawaysResponse,
  QaPairsResponse,
  SectionRequest,
} from '@/types/schemas/api'

// ─── Request / response types ───

export interface SummarizeSecFilingRequest {
  accession_no: string
  ticker?: string
  /** Pass true to bypass the cache and force regeneration. */
  force?: boolean
}

export interface SummarizeNewsRequest {
  vendor: string
  hash?: string
  story_url?: string
  news_id?: string
  headline?: string
  ticker?: string
  topics?: string[]
  is_pr?: boolean
  content?: string
  /** Pass true to bypass the cache and force regeneration. */
  force?: boolean
}

export interface NewsArticleDocumentRequest {
  vendor: string
  news_id: string
  story_url?: string
}

export interface SummarizeTranscriptRequest {
  ticker: string
  fiscal_year: number
  quarter: number
  /** Pass true to bypass the cache; subject to a per-transcript cooldown. */
  force?: boolean
}

export interface NewsArticleDocument {
  html: string
  source: 's3' | 'benzinga'
}

/**
 * RTK Query API slice for the Edge Research service.
 * Auth: accessToken (Cognito Bearer — edge-research validates `token_use=="access"`,
 *       same as edgeUsersApi / edge-llm-api / edge-user-api).
 *
 * Currently exposes:
 *   summarizeNews — POST /v1/news/summarize
 *     Get-or-generate a news article summary. The server checks the
 *     DynamoDB cache and regenerates via edge-llm-api if missing/stale.
 *     Pass `force: true` to trigger an unconditional regeneration.
 */
export const edgeResearchApi = createApi({
  reducerPath: 'edgeResearchApi',
  baseQuery: createAuthBaseQuery(COMPANION_API, 'access'),
  tagTypes: ['NewsSummary', 'DeepResearchSession'],
  endpoints: (builder) => ({
    summarizeNews: builder.mutation<NewsSummary, SummarizeNewsRequest>({
      query: (body) => ({
        url: '/v1/news/summarize',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => NewsSummarySchema.parse(raw),
    }),
    summarizeSecFiling: builder.mutation<SecFilingSummary, SummarizeSecFilingRequest>({
      query: (body) => ({
        url: '/v1/sec/filings/summarize',
        method: 'POST',
        body,
      }),
      transformErrorResponse: (error, meta) => ({
        ...error,
        data: {
          ...(error.data as object),
          retry_after: (meta as { response?: Response } | undefined)?.response?.headers.get(
            'Retry-After',
          ),
        },
      }),
      transformResponse: (raw) => SecFilingSummarySchema.parse(raw),
    }),
    getNewsArticleDocument: builder.query<NewsArticleDocument, NewsArticleDocumentRequest>({
      query: ({ vendor, news_id, story_url }) => {
        const params = new URLSearchParams({ vendor, news_id })
        if (story_url) params.set('story_url', story_url)
        return `/v1/news/document?${params.toString()}`
      },
      transformResponse: (raw) => raw as NewsArticleDocument,
      // A published article document is immutable — keep it cached for a day so
      // reopening the same story never re-fetches (default is only 60s).
      keepUnusedDataFor: 86400,
    }),
    getTickerSummary: builder.query<TickerSummary, string>({
      query: (ticker) => ({
        url: `/v1/ticker/summary/${encodeURIComponent(ticker)}`,
      }),
      transformResponse: (raw) => TickerSummarySchema.parse(raw),
      // 404 = "no summary yet"; surface as an error the caller treats as empty,
      // not a toast. RTK Query exposes the status on the error object.
      // It's an LLM-derived DAILY rollup, so cache it for an hour — re-viewing a
      // ticker serves from the RTK cache instead of re-fetching (and re-running
      // the summary server-side). Default keepUnusedDataFor is only 60s.
      keepUnusedDataFor: 3600,
    }),
    summarizeTranscript: builder.mutation<TranscriptEnrichment, SummarizeTranscriptRequest>({
      query: (body) => ({
        url: '/v1/transcripts/summarize',
        method: 'POST',
        body,
      }),
      // Server's response is a superset of TranscriptEnrichment (adds
      // origin/status), and TranscriptEnrichmentSchema accepts unknowns —
      // safe to reuse here so call-sites see the same shape they got from
      // the legacy `useGetFmpTranscriptEnrichmentQuery`.
      transformResponse: (raw) => TranscriptEnrichmentSchema.parse(raw),
    }),
    summarizeTranscriptHighlights: builder.mutation<
      TranscriptHighlights,
      SummarizeTranscriptHighlightsRequest
    >({
      query: (body) => ({
        url: '/v1/transcripts/highlights',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => TranscriptHighlightsSchema.parse(raw),
    }),
    fetchTranscriptKeyHighlights: builder.mutation<KeyHighlightsResponse, SectionRequest>({
      query: (body) => ({
        url: '/v1/transcripts/key-highlights',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => KeyHighlightsResponseSchema.parse(raw),
    }),
    fetchTranscriptAutoDetectedHighlights: builder.mutation<
      AutoDetectedHighlightsResponse,
      SectionRequest
    >({
      query: (body) => ({
        url: '/v1/transcripts/auto-detected-highlights',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => AutoDetectedHighlightsResponseSchema.parse(raw),
    }),
    fetchTranscriptTakeaways: builder.mutation<TakeawaysResponse, SectionRequest>({
      query: (body) => ({
        url: '/v1/transcripts/takeaways',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => TakeawaysResponseSchema.parse(raw),
    }),
    fetchTranscriptQaPairs: builder.mutation<QaPairsResponse, SectionRequest>({
      query: (body) => ({
        url: '/v1/transcripts/qa-pairs',
        method: 'POST',
        body,
      }),
      transformResponse: (raw) => QaPairsResponseSchema.parse(raw),
    }),
  }),
})

export const {
  useSummarizeNewsMutation,
  useSummarizeSecFilingMutation,
  useLazyGetNewsArticleDocumentQuery,
  useGetTickerSummaryQuery,
  useSummarizeTranscriptMutation,
  useSummarizeTranscriptHighlightsMutation,
  useFetchTranscriptKeyHighlightsMutation,
  useFetchTranscriptAutoDetectedHighlightsMutation,
  useFetchTranscriptTakeawaysMutation,
  useFetchTranscriptQaPairsMutation,
} = edgeResearchApi

/**
 * Alias kept for call-site backward compatibility.
 * Equivalent to `useSummarizeNewsMutation`.
 */
export const useGenerateNewsSummaryMutation = useSummarizeNewsMutation

// ─── Deep Research sessions ───
//
// The persisted session index for the Deep Research page (F3). Runs stream over
// SSE (see use-deep-research-run.ts); the session list/detail/rename/delete are
// plain REST and belong on this same RTK Query slice (COMPANION_API + access
// token). Injected rather than inlined above to keep the deep-research concern
// grouped; the `DeepResearchSession` tag is declared on the base `tagTypes`.

/** List rows are LIST-tagged; each detail is tagged by id so a rename/delete
 * invalidates precisely. */
export const edgeResearchSessionsApi = edgeResearchApi.injectEndpoints({
  endpoints: (builder) => ({
    listDeepResearchSessions: builder.query<DeepResearchSession[], void>({
      query: () => ({ url: '/v1/deep-research/sessions' }),
      transformResponse: (raw) => DeepResearchSessionListResponseSchema.parse(raw),
      providesTags: (result) =>
        result
          ? [
              ...result.map((s) => ({ type: 'DeepResearchSession' as const, id: s.session_id })),
              { type: 'DeepResearchSession' as const, id: 'LIST' },
            ]
          : [{ type: 'DeepResearchSession' as const, id: 'LIST' }],
    }),
    getDeepResearchSession: builder.query<DeepResearchSessionDetail, string>({
      query: (sessionId) => ({
        url: `/v1/deep-research/sessions/${encodeURIComponent(sessionId)}`,
      }),
      transformResponse: (raw) => DeepResearchSessionDetailResponseSchema.parse(raw),
      providesTags: (_result, _err, sessionId) => [{ type: 'DeepResearchSession', id: sessionId }],
    }),
    renameDeepResearchSession: builder.mutation<
      { ok: boolean },
      { sessionId: string; title: string }
    >({
      query: ({ sessionId, title }) => ({
        url: `/v1/deep-research/sessions/${encodeURIComponent(sessionId)}`,
        method: 'PATCH',
        body: { title },
      }),
      // Backend replies {ok: true}; the tag invalidation below refetches the
      // list, so the mutation body carries no session payload.
      invalidatesTags: (_result, _err, { sessionId }) => [
        { type: 'DeepResearchSession', id: sessionId },
        { type: 'DeepResearchSession', id: 'LIST' },
      ],
    }),
    // A fork: a new session under this one, branching after a run. `continue`
    // keeps the model context through that turn; `regenerate` starts it empty.
    forkDeepResearchSession: builder.mutation<
      { session_id: string },
      { sessionId: string; afterRunId: string; mode: 'continue' | 'regenerate' }
    >({
      query: ({ sessionId, afterRunId, mode }) => ({
        url: `/v1/deep-research/sessions/${encodeURIComponent(sessionId)}/fork`,
        method: 'POST',
        body: { after_run_id: afterRunId, mode },
      }),
      invalidatesTags: [{ type: 'DeepResearchSession', id: 'LIST' }],
    }),
    searchDeepResearch: builder.query<SearchHits, string>({
      query: (q) => ({ url: `/v1/deep-research/search?q=${encodeURIComponent(q)}` }),
      transformResponse: (raw) => SearchHitsSchema.parse(raw),
    }),
    deleteDeepResearchSession: builder.mutation<{ success: boolean }, string>({
      query: (sessionId) => ({
        url: `/v1/deep-research/sessions/${encodeURIComponent(sessionId)}`,
        method: 'DELETE',
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: (_result, _err, sessionId) => [
        { type: 'DeepResearchSession', id: sessionId },
        { type: 'DeepResearchSession', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useListDeepResearchSessionsQuery,
  useGetDeepResearchSessionQuery,
  useLazyGetDeepResearchSessionQuery,
  useRenameDeepResearchSessionMutation,
  useDeleteDeepResearchSessionMutation,
} = edgeResearchSessionsApi

// ─── Saved Deep Research reports ────────────────────────────────────────────
// A report is read from its run's journal; SAVING copies a self-contained
// document server-side so it outlives the chat (liquibase pg 1.52).

export const edgeResearchReportsApi = edgeResearchApi
  .enhanceEndpoints({ addTagTypes: ['DeepResearchReport'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      listDeepResearchReports: builder.query<SavedReportSummary[], void>({
        query: () => ({ url: '/v1/deep-research/reports' }),
        transformResponse: (raw) => SavedReportListResponseSchema.parse(raw),
        providesTags: [{ type: 'DeepResearchReport', id: 'LIST' }],
      }),
      getDeepResearchReport: builder.query<SavedReport, string>({
        query: (reportId) => ({
          url: `/v1/deep-research/reports/${encodeURIComponent(reportId)}`,
        }),
        transformResponse: (raw) => SavedReportResponseSchema.parse(raw),
        providesTags: (_r, _e, reportId) => [{ type: 'DeepResearchReport', id: reportId }],
      }),
      // A run's stored panel data (liquibase pg 1.54 `runs.snapshots`): read
      // when a turn's report opens, written as its visuals paint. The PUT
      // merges into the cached GET so a reopen inside the cache window reads
      // the capture back instead of querying the warehouse again.
      getDeepResearchRunSnapshots: builder.query<Record<string, VisualSnapshot>, string>({
        query: (runId) => ({
          url: `/v1/deep-research/runs/${encodeURIComponent(runId)}/snapshots`,
        }),
        transformResponse: (raw) => RunSnapshotsResponseSchema.parse(raw),
        keepUnusedDataFor: 3600,
      }),
      putDeepResearchRunSnapshots: builder.mutation<
        { ok: boolean; stored: boolean },
        { runId: string; snapshots: Record<string, VisualSnapshot> }
      >({
        query: ({ runId, snapshots }) => ({
          url: `/v1/deep-research/runs/${encodeURIComponent(runId)}/snapshots`,
          method: 'PUT',
          body: { snapshots },
        }),
        onQueryStarted({ runId, snapshots }, { dispatch }) {
          dispatch(
            edgeResearchReportsApi.util.updateQueryData(
              'getDeepResearchRunSnapshots',
              runId,
              (draft) => {
                Object.assign(draft, snapshots)
              },
            ),
          )
        },
      }),
      saveDeepResearchReport: builder.mutation<
        { report_id: string },
        {
          runId: string
          sessionId?: string
          /** Per visual id: what its panel fetched, so the saved report renders without a query. */
          snapshots?: Record<string, VisualSnapshot>
        }
      >({
        query: ({ runId, snapshots }) => ({
          url: `/v1/deep-research/runs/${encodeURIComponent(runId)}/report`,
          method: 'POST',
          body: { snapshots: snapshots ?? {} },
        }),
        invalidatesTags: (_r, _e, { sessionId }) => [
          { type: 'DeepResearchReport', id: 'LIST' },
          ...(sessionId ? [{ type: 'DeepResearchSession' as const, id: sessionId }] : []),
        ],
      }),
      // DELETE archives on the server: reports are never deleted, only hidden
      // from the owner's lists (kept for system learning). Re-saving the run
      // brings the report back.
      // Share by link: mint (or fetch the existing) token; revoke it.
      shareDeepResearchReport: builder.mutation<{ share_token: string }, string>({
        query: (reportId) => ({
          url: `/v1/deep-research/reports/${encodeURIComponent(reportId)}/share`,
          method: 'POST',
        }),
        invalidatesTags: (_r, _e, reportId) => [{ type: 'DeepResearchReport', id: reportId }],
      }),
      unshareDeepResearchReport: builder.mutation<{ ok: boolean }, string>({
        query: (reportId) => ({
          url: `/v1/deep-research/reports/${encodeURIComponent(reportId)}/share`,
          method: 'DELETE',
        }),
        invalidatesTags: (_r, _e, reportId) => [{ type: 'DeepResearchReport', id: reportId }],
      }),
      archiveDeepResearchReport: builder.mutation<
        { ok: boolean; archived: boolean },
        { reportId: string; sessionId?: string | null }
      >({
        query: ({ reportId }) => ({
          url: `/v1/deep-research/reports/${encodeURIComponent(reportId)}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_r, _e, { reportId, sessionId }) => [
          { type: 'DeepResearchReport', id: 'LIST' },
          { type: 'DeepResearchReport', id: reportId },
          ...(sessionId ? [{ type: 'DeepResearchSession' as const, id: sessionId }] : []),
        ],
      }),
    }),
  })

export const { useForkDeepResearchSessionMutation, useSearchDeepResearchQuery } =
  edgeResearchSessionsApi

export const {
  useListDeepResearchReportsQuery,
  useGetDeepResearchReportQuery,
  useSaveDeepResearchReportMutation,
  useArchiveDeepResearchReportMutation,
  useShareDeepResearchReportMutation,
  useUnshareDeepResearchReportMutation,
  useGetDeepResearchRunSnapshotsQuery,
  usePutDeepResearchRunSnapshotsMutation,
} = edgeResearchReportsApi
